import type {
  CheckResult,
  HistoryRange,
  HistoryResponse,
  Incident,
  MaintenanceResponse,
} from "@central-hub/contracts";
import { useEffect, useRef, useState } from "react";
import { api } from "./api";
import type { Session } from "./auth";
import { HistoryChart, StatusLegend } from "./Charts";
import { bucketLabel } from "./chart-math";
import { clock, duration } from "./format";
import { Icon } from "./icons";
import { KubeDetail } from "./KubeDetail";
import { MaintenanceSection } from "./Maintenance";
import {
  deployRows,
  formatUptime,
  monthLabel,
  type ProblemWindow,
  recentMonths,
  reportHref,
  STATUS_LABEL,
  type TargetCard,
} from "./model";
import { ProblemPanel } from "./Problems";
import { SheetSection } from "./Sheet";

const RANGES: { value: HistoryRange; label: string }[] = [
  { value: "24h", label: "24 h" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
];
const REFRESH_MS = 60_000;
/** An incident opens after a few failures: start a little earlier to include the first ones. */
const INCIDENT_LEAD_MS = 10 * 60_000;

interface Loaded {
  range: HistoryRange;
  histories: HistoryResponse[];
  incidents: Incident[];
  /** Runs of the deploy workflow's check, newest first; empty without one. */
  deploys: NonNullable<CheckResult["data"]>[];
}

/** Everything about one target: per-check history charts, availability and its incidents. */
export function Detail({
  card,
  session,
  now,
  canDeploy = false,
  maintenance = null,
  isAdmin = false,
  onMaintenanceChange = () => {},
  onClose,
}: {
  card: TargetCard;
  session: Session;
  now: number;
  /** Whether the viewer may deploy this target: shows the deploy link and rollback commands. */
  canDeploy?: boolean;
  maintenance?: MaintenanceResponse | null;
  /** Admins may hold this target's alerts for a while. */
  isAdmin?: boolean;
  onMaintenanceChange?: () => void;
  onClose(): void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [range, setRange] = useState<HistoryRange>("24h");
  const [data, setData] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** What the reader is inspecting, and where: under a chart, or under the incidents. */
  const [inspect, setInspect] = useState<{
    window: ProblemWindow;
    place: "chart" | "incidents";
  } | null>(null);
  const checkNames = card.checks.map((c) => c.name).join("\u0000");
  const deployCheck = card.deploy?.check;

  useEffect(() => {
    const element = dialog.current;
    if (element && !element.open) element.showModal();
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const token = await session.getToken();
        if (!token) throw new Error("session expired");
        const names = checkNames ? checkNames.split("\u0000") : [];
        const [histories, incidents, runs] = await Promise.all([
          Promise.all(names.map((check) => api.history(token, card.name, check, range))),
          api.targetIncidents(token, card.name),
          deployCheck ? api.runs(token, card.name, deployCheck) : null,
        ]);
        if (!cancelled) {
          setData({
            range,
            histories,
            incidents: incidents.incidents,
            deploys: runs?.runs.map((r) => r.data) ?? [],
          });
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "could not load history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [session, card.name, checkNames, range, deployCheck]);

  const label = STATUS_LABEL[card.status];
  return (
    <dialog
      ref={dialog}
      className="detail"
      aria-labelledby="detail-title"
      onClose={onClose}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
    >
      <header className="detail-head" data-status={card.status}>
        <Icon name={card.status} label={label} />
        <h2 id="detail-title">{card.name}</h2>
        {card.problem && <span className="flag">{label}</span>}
        <span className="detail-uptime num" title="Worst 30-day availability of its checks">
          30 d {formatUptime(card.uptime30d)}
        </span>
        <button type="button" className="iconbtn" onClick={onClose} aria-label="Close details">
          <Icon name="close" />
        </button>
      </header>

      <div className="detail-toolbar">
        <fieldset className="segmented">
          <legend className="sr-only">Time range</legend>
          {RANGES.map((r) => (
            <button
              key={r.value}
              type="button"
              aria-pressed={range === r.value}
              onClick={() => setRange(r.value)}
            >
              {r.label}
            </button>
          ))}
        </fieldset>
        <StatusLegend />
        {error && (
          <p className="summary-warn" role="alert">
            Couldn’t refresh: {error}
          </p>
        )}
      </div>

      <div className="detail-body" data-loading={loading && data !== null}>
        {!data && !error && <p className="muted">Loading history…</p>}
        {card.checks.map((check) => {
          const history = data?.histories.find((h) => h.check === check.name);
          return (
            <section key={check.name} className="detail-check" data-status={check.status}>
              <div className="detail-check-head">
                <Icon name={check.status} label={STATUS_LABEL[check.status]} />
                <h3 className="mono">{check.name}</h3>
                <p className="detail-check-msg ellipsis" title={check.message}>
                  {check.message}
                </p>
                {check.link && (
                  <a
                    className="detail-check-link"
                    href={check.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open ↗
                  </a>
                )}
                <p className="chips num">
                  <span>24 h {formatUptime(check.uptime24h)}</span>
                  <span>30 d {formatUptime(check.uptime30d)}</span>
                </p>
              </div>
              {history && (
                <HistoryChart
                  title={`${card.name} ${check.name}`}
                  points={history.points}
                  range={history.range}
                  from={Date.parse(history.from)}
                  to={Date.parse(history.to)}
                  bucketMs={history.bucketMs}
                  selected={
                    inspect?.place === "chart" && inspect.window.check === check.name
                      ? history.points.findIndex((p) => Date.parse(p.t) === inspect.window.from)
                      : null
                  }
                  onSelect={(i) => {
                    const t = Date.parse(history.points[i]?.t ?? "");
                    setInspect({
                      place: "chart",
                      window: {
                        check: check.name,
                        from: t,
                        to: t + history.bucketMs,
                        label: bucketLabel(t, history.bucketMs),
                      },
                    });
                  }}
                />
              )}
              {inspect?.place === "chart" && inspect.window.check === check.name && (
                <ProblemPanel
                  target={card.name}
                  window={inspect.window}
                  session={session}
                  onClose={() => setInspect(null)}
                />
              )}
            </section>
          );
        })}

        {card.version && (
          <section className="detail-section">
            <h3 className="eyebrow">Deployed version</h3>
            <ul>
              <li className="detail-cert detail-version">
                <span className="mono">{card.version.running}</span>
                <span>
                  {!card.version.branch
                    ? card.version.commit
                      ? `commit ${card.version.commit.slice(0, 7)}`
                      : "not tied to a commit"
                    : card.version.branch.upToDate
                      ? "up to date with the branch"
                      : `branch is at ${card.version.branch.head.slice(0, 7)}: not deployed yet`}
                </span>
                <span className="detail-version-links">
                  {card.version.branch?.compare && (
                    <a
                      className="detail-check-link"
                      href={card.version.branch.compare}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Compare ↗
                    </a>
                  )}
                  {canDeploy && card.version.deployUrl && (
                    <a
                      className="detail-check-link"
                      href={card.version.deployUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Opens the deploy workflow on GitHub, where it is run"
                    >
                      Deploy ↗
                    </a>
                  )}
                </span>
              </li>
            </ul>
            {canDeploy && card.version.rollback && (
              <>
                <h4 className="detail-subhead">Roll back to an earlier build</h4>
                <ul>
                  {card.version.rollback.map((r) => (
                    <li key={r.tag} className="detail-cert detail-version">
                      <span className="mono">{r.tag}</span>
                      <code className="detail-command ellipsis" title={r.command}>
                        {r.command}
                      </code>
                      <CopyButton text={r.command} />
                    </li>
                  ))}
                </ul>
              </>
            )}
          </section>
        )}

        {card.deploy && (
          <section className="detail-section">
            <h3 className="eyebrow">Deploys</h3>
            {card.deploy.running && (
              <p className="detail-deploying" aria-live="polite">
                Deploying now · started {duration(now - card.deploy.running.since)} ago
                {card.deploy.running.url && (
                  <>
                    {" · "}
                    <a
                      className="detail-check-link"
                      href={card.deploy.running.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View ↗
                    </a>
                  </>
                )}
              </p>
            )}
            {data && data.deploys.length === 0 && <p className="muted">No deploy recorded yet.</p>}
            <ul>
              {data &&
                deployRows(data.deploys, now).map((d) => (
                  <li key={d.key} data-status={d.status} className="detail-cert detail-deploy">
                    <Icon name={d.status} label={d.conclusion} />
                    <span className="mono">{d.commit}</span>
                    <span className="ellipsis">
                      {d.conclusion} {d.when}
                      {d.duration && ` · took ${d.duration}`}
                      {d.actor && ` · ${d.actor}`}
                    </span>
                    {d.url && (
                      <a
                        className="detail-check-link"
                        href={d.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Run ↗
                      </a>
                    )}
                  </li>
                ))}
            </ul>
          </section>
        )}

        {card.certificates.length > 0 && (
          <section className="detail-section">
            <h3 className="eyebrow">Certificates</h3>
            <ul>
              {card.certificates.map((c) => (
                <li key={c.check} data-status={c.status} className="detail-cert">
                  <Icon name={c.status} label={STATUS_LABEL[c.status]} />
                  <span className="mono">{c.check}</span>
                  <span>{c.text}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {card.backups.length > 0 && (
          <section className="detail-section">
            <h3 className="eyebrow">Backups</h3>
            <ul>
              {card.backups.map((b) => (
                <li key={b.check} data-status={b.status} className="detail-cert">
                  <Icon name={b.status} label={STATUS_LABEL[b.status]} />
                  <span className="mono">{b.check}</span>
                  <span>{b.text}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <SheetSection sheet={card.sheet} />

        <section className="detail-section">
          <h3 className="eyebrow">Quick look</h3>
          <p className="detail-version-links">
            {recentMonths(now, 3).map((m) => (
              <a key={m} href={reportHref(card.name, m)}>
                {monthLabel(m)}
              </a>
            ))}
          </p>
        </section>

        <MaintenanceSection
          target={card.name}
          session={session}
          maintenance={maintenance}
          isAdmin={isAdmin}
          onChange={onMaintenanceChange}
        />

        {card.checks.some((c) => c.name === "kubernetes:pods") && (
          <KubeDetail
            target={card.name}
            session={session}
            now={now}
            canReadLogs={canDeploy}
            {...(card.limits && { limits: card.limits })}
          />
        )}

        <section className="detail-section">
          <h3 className="eyebrow">Incidents</h3>
          {data && data.incidents.length === 0 && <p className="muted">No incidents recorded.</p>}
          <ul className="detail-incidents">
            {data?.incidents.map((i) => {
              const opened = Date.parse(i.openedAt);
              const end = i.resolvedAt ? Date.parse(i.resolvedAt) : now;
              return (
                <li key={i.id} data-status={i.status}>
                  <Icon name={i.status} label={STATUS_LABEL[i.status]} />
                  <span className="mono">{i.check}</span>
                  <span className="ellipsis" title={i.message}>
                    {i.message ?? ""}
                  </span>
                  <span className="num muted">
                    {new Date(opened).toLocaleDateString(undefined, {
                      day: "numeric",
                      month: "short",
                    })}{" "}
                    {clock(opened)}
                  </span>
                  <span className="num">
                    {i.resolvedAt
                      ? `${duration(end - opened)}`
                      : `ongoing · ${duration(end - opened)}`}
                  </span>
                  <button
                    type="button"
                    className="linkbtn"
                    aria-label={`What happened: ${i.check} incident from ${clock(opened)}`}
                    onClick={() =>
                      setInspect({
                        place: "incidents",
                        window: {
                          check: i.check,
                          from: opened - INCIDENT_LEAD_MS,
                          to: end + 60_000,
                          label: `incident from ${clock(opened)}`,
                        },
                      })
                    }
                  >
                    Details
                  </button>
                </li>
              );
            })}
          </ul>
          {inspect?.place === "incidents" && (
            <ProblemPanel
              target={card.name}
              window={inspect.window}
              session={session}
              onClose={() => setInspect(null)}
            />
          )}
        </section>
      </div>
    </dialog>
  );
}

/** Copies a command; the hub only shows it, running it stays with whoever has cluster access. */
function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="linkbtn"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}
